'use client'

import { useEffect, useRef, useState } from 'react'
import type { RoundPhase } from '@/types/game'
import { stopAllAudio, trackAudio } from '@/lib/audio-registry'

// ── Track manifest ────────────────────────────────────────────────────────────
// Each looping track fades in/out as the phase changes.
// One-shot stings are played immediately and stop after they finish.

const LOOP_TRACKS: Record<string, string> = {
  lobby:      '/sounds/ambient-lobby.mp3',
  infection:  '/sounds/infection-phase.mp3',
  discussion: '/sounds/discussion-phase.mp3',
  voting:     '/sounds/voting-phase.mp3',
}

// One-shot stings played on phase entry (do not loop)
const STING_TRACKS: Partial<Record<RoundPhase | 'lobby' | 'clean-win', string>> = {
  reveal:      '/sounds/reveal-sting.mp3',
  'clean-win': '/sounds/reveal-sting.mp3',
}

// Played once at game-over
export const GAME_OVER_TRACKS = {
  infected: '/sounds/infected-win.mp3',
  clean:    '/sounds/reveal-sting.mp3',
}

const FADE_DURATION_MS = 1500
const BASE_VOLUME = 0.35

/** Post-game bed. See the `ended` branch in the scene effect. */
const ENDED_TRACK = LOOP_TRACKS.lobby
const ENDED_VOLUME = BASE_VOLUME * 0.6

// ── Web Audio ─────────────────────────────────────────────────────────────────
//
// This used HTMLAudioElement with `loop = true`. Two problems, both phone-only:
//
//   1. Elements unlock individually on mobile, so an element built mid-game with
//      no gesture since had play() rejected — one track played all game.
//   2. MP3 carries encoder padding, so every loop restart is an audible gap.
//      `discussion-phase.mp3` is 6.3s against a 180s discussion phase: it
//      restarts ~29 times, which is the stuttering that was reported. Desktop
//      decoders paper over it; mobile ones do not.
//
// Web Audio fixes both by construction. One AudioContext is unlocked once, and
// looping a decoded AudioBuffer has no re-seek and no padding — it is sample
// accurate, so a 6-second bed is genuinely seamless. Gain ramps are scheduled on
// the audio clock rather than driven by setInterval, so a backgrounded tab
// throttling timers can no longer strand a fade half-finished.

let ctx: AudioContext | null = null
let loopGain: GainNode | null = null
let loopSource: AudioBufferSourceNode | null = null
/** A source ramping out but not yet stopped. See fadeOutLoop / killFadingSource. */
let fadingSource: AudioBufferSourceNode | null = null
let loopToken = 0
const buffers = new Map<string, Promise<AudioBuffer>>()

let hasGesture = false
const gestureWaiters = new Set<() => void>()

function loadBuffer(src: string): Promise<AudioBuffer> {
  const cached = buffers.get(src)
  if (cached) return cached
  // The in-flight promise is cached, not just the result, so a phase that
  // changes twice quickly cannot start two downloads of the same track.
  const p = fetch(src)
    .then(r => r.arrayBuffer())
    .then(b => ctx!.decodeAudioData(b))
  buffers.set(src, p)
  p.catch(() => buffers.delete(src))
  return p
}

function buildContext() {
  if (ctx) return
  type WithWebkit = typeof globalThis & { webkitAudioContext?: typeof AudioContext }
  const Ctor = window.AudioContext ?? (globalThis as WithWebkit).webkitAudioContext
  if (!Ctor) return
  ctx = new Ctor()
  loopGain = ctx.createGain()
  loopGain.gain.value = 0
  loopGain.connect(ctx.destination)
  void ctx.resume()
}

function armGestureListener() {
  if (typeof window === 'undefined' || hasGesture) return

  // `touchstart` is deliberately absent. Chrome and Brave exclude it from user
  // activation on purpose — so that scrolling a page cannot unlock audio —
  // while iOS Safari accepts it. With `touchstart` armed as a one-shot, a
  // single scroll on Android fired the handler, marked the gesture consumed and
  // removed every listener, leaving a context that could never resume: the
  // lobby was silent for the rest of the session and no later tap retried.
  const events = ['pointerdown', 'pointerup', 'touchend', 'click', 'keydown'] as const

  const handler = () => {
    if (hasGesture) return
    buildContext()   // must be created inside the gesture on iOS
    unlockBed()      // elements unlock individually — this one needs the gesture too
    if (!ctx) return

    // The gesture only counts once the context is actually running. Anything
    // the browser declined to treat as activation leaves us armed for the next
    // one, instead of burning the unlock on it.
    void ctx.resume().then(() => {
      if (hasGesture || ctx?.state !== 'running') return
      hasGesture = true
      for (const w of gestureWaiters) w()
      gestureWaiters.clear()
      for (const e of events) window.removeEventListener(e, handler)
    }).catch(() => { /* not a real activation — stay armed */ })
  }

  for (const e of events) window.addEventListener(e, handler, { passive: true })
}

function useHasGesture(): boolean {
  const [ready, setReady] = useState(hasGesture)
  useEffect(() => {
    if (hasGesture) { setReady(true); return }
    const wake = () => setReady(true)
    gestureWaiters.add(wake)
    armGestureListener()
    return () => { gestureWaiters.delete(wake) }
  }, [])
  return ready
}

function rampGain(target: number) {
  if (!ctx || !loopGain) return
  const now = ctx.currentTime
  loopGain.gain.cancelScheduledValues(now)
  loopGain.gain.setValueAtTime(loopGain.gain.value, now)
  loopGain.gain.linearRampToValueAtTime(target, now + FADE_DURATION_MS / 1000)
}

function stopLoop() {
  if (loopSource) {
    try { loopSource.stop() } catch { /* already stopped */ }
    loopSource.disconnect()
    loopSource = null
  }
}

/** Milliseconds of ramp before the loop is cut. Short enough to read as "off". */
const STOP_FADE_MS = 90

/**
 * Leave-the-page version of stopLoop: ramp to silence, *then* stop.
 *
 * `stopLoop` cuts the buffer mid-waveform at full gain, which is a step
 * discontinuity — audible as a sharp click when navigating away from the lobby
 * or a game on mobile. Zeroing the gain afterwards was too late to prevent it.
 *
 * Both the ramp and the stop are scheduled on the audio clock, so they complete
 * on the audio thread after React has torn the component down. The module-level
 * context outlives the component, which is what makes that safe. Disconnection
 * waits for `onended` so nothing is unplugged mid-sound either.
 *
 * Not used when switching phase loops: those share one gain node, so a fade
 * there would be overwritten by the incoming track's fade-in. Only correct
 * where nothing follows.
 */
function fadeOutLoop() {
  if (!ctx || !loopGain) { stopLoop(); return }
  const now   = ctx.currentTime
  const endAt = now + STOP_FADE_MS / 1000

  loopGain.gain.cancelScheduledValues(now)
  loopGain.gain.setValueAtTime(loopGain.gain.value, now)
  loopGain.gain.linearRampToValueAtTime(0, endAt)

  const source = loopSource
  loopSource = null
  if (!source) return
  fadingSource = source
  source.onended = () => {
    if (fadingSource === source) fadingSource = null
    try { source.disconnect() } catch { /* already gone */ }
  }
  try { source.stop(endAt) } catch { stopLoop() }
}

/**
 * Cut a still-fading source immediately.
 *
 * A fade-out survives the component that started it, so a route change back
 * within STOP_FADE_MS would leave the old track feeding the shared gain node
 * while the new one ramps up on it — the incoming `rampGain` cancels the
 * outgoing fade, so both play. Rare and quiet, but unbounded if the player
 * bounces between pages, and there is only one gain node to share.
 */
function killFadingSource() {
  if (!fadingSource) return
  try { fadingSource.stop() } catch { /* already stopped */ }
  try { fadingSource.disconnect() } catch { /* already gone */ }
  fadingSource = null
}

// ── The ambient bed stays on an HTMLAudioElement, deliberately ───────────────
//
// Moving every loop to Web Audio (fe5c17f) fixed the discussion-phase stutter
// and introduced a beep when an installed PWA is backgrounded on iOS: measured
// at 375 Hz with harmonics past 12 kHz, and 48000/128 = 375 — the graph's last
// render quantum, repeating. iOS freezes the page before visibilitychange or
// pagehide can run, so the renderer stops while the output device keeps pulling
// and the hardware loops whatever it last had. Three attempts to silence it from
// those handlers failed for that reason: the handler never runs.
//
// A media element has no such failure mode — iOS owns its lifecycle and pauses
// it cleanly on suspend, which is why this only started today.
//
// So the bed goes back to an element and the phase loops stay on Web Audio.
// That split is not a compromise, it is what each track needs: the bed is
// ambient-lobby.mp3 at 21.7s, whose loop seam is rare and unremarkable (it
// shipped that way for months), while discussion-phase.mp3 is 6.3s against a
// 180s phase — ~29 restarts, and the reason the migration happened at all.
//
// One persistent element, not one per scene: elements unlock individually on
// mobile, so a fresh element built with no recent gesture has play() rejected.
// This one is unlocked with the context on the first gesture and reused.
let bedEl: HTMLAudioElement | null = null

function getBed(): HTMLAudioElement | null {
  if (typeof window === 'undefined') return null
  if (!bedEl) {
    bedEl = trackAudio(new Audio(ENDED_TRACK))
    bedEl.loop = true
    bedEl.preload = 'auto'
  }
  return bedEl
}

/**
 * Unlock the bed inside a real gesture, the same way the context is built there.
 *
 * A muted play/pause is enough to mark the element as user-permitted, after
 * which later play() calls succeed with no gesture in hand — the same trick
 * primeArenaSounds uses for the door creak.
 */
function unlockBed() {
  const el = getBed()
  if (!el || !el.paused) return
  const wasMuted = el.muted
  el.muted = true
  el.play()
    .then(() => { el.pause(); el.muted = wasMuted })
    .catch(() => { el.muted = wasMuted })
}

/**
 * Ramp the bed's volume, since an element has no audio-clock automation.
 *
 * The Web Audio path faded over FADE_DURATION_MS and the bed has to match, or
 * the lobby slams in at full level — which is both a worse entrance than the
 * one it replaces and a discontinuity of its own. Driven by rAF, which is fine
 * here: every caller is a live page mid-navigation, never a frozen one.
 */
let bedRamp = 0

/**
 * iOS ignores HTMLMediaElement.volume — it is effectively read-only there, and
 * assignments are dropped silently rather than throwing. `muted` is honoured.
 *
 * So volume alone cannot mute the bed on a phone: the mute button flipped its
 * icon while the track played straight through. Everything below drives
 * `muted` as the authority and treats the volume ramp as enhancement for the
 * platforms that implement it.
 */
let volumeSettable: boolean | null = null
function canSetVolume(el: HTMLAudioElement): boolean {
  if (volumeSettable !== null) return volumeSettable
  const prev = el.volume
  try {
    el.volume = 0.123
    volumeSettable = Math.abs(el.volume - 0.123) < 0.001
    el.volume = prev
  } catch {
    volumeSettable = false
  }
  return volumeSettable
}

function rampBed(target: number, ms = FADE_DURATION_MS, onDone?: () => void) {
  const el = getBed()
  if (!el) return
  const token = ++bedRamp
  const to = Math.max(0, Math.min(1, target))

  // The authority, and the only lever iOS respects.
  el.muted = to === 0

  if (!canSetVolume(el)) {
    // No gradual fade available — the level is whatever the asset was mastered
    // at. Silence still lands, which is the part that matters.
    onDone?.()
    return
  }

  const from = el.volume
  const t0 = performance.now()
  const step = () => {
    if (token !== bedRamp) return          // a newer ramp took over
    const k = Math.min(1, (performance.now() - t0) / ms)
    el.volume = from + (to - from) * k
    if (k < 1) { requestAnimationFrame(step); return }
    onDone?.()
  }
  requestAnimationFrame(step)
}

function playBed(target: number) {
  const el = getBed()
  if (!el) return
  if (el.paused) {
    el.volume = 0
    el.play().catch(() => { /* no gesture yet — the listener retries */ })
  }
  rampBed(target)
}

function stopBed() {
  const el = bedEl
  if (!el || el.paused) return
  rampBed(0, 250, () => {
    el.pause()
    try { el.currentTime = 0 } catch { /* not seekable yet */ }
  })
}

async function playLoop(src: string, target: number) {
  if (!ctx || !loopGain) return
  const token = ++loopToken
  const buffer = await loadBuffer(src).catch(() => null)
  // A newer scene arrived while this was decoding — drop it. Without the token
  // a slow first load could start under whatever phase came next.
  if (!buffer || token !== loopToken || !ctx || !loopGain) return
  stopLoop()
  killFadingSource()   // nothing from a previous page may bleed into this one
  const source = ctx.createBufferSource()
  source.buffer = buffer
  source.loop = true          // sample-accurate, no gap at the seam
  source.connect(loopGain)
  source.start()
  loopSource = source
  loopGain.gain.setValueAtTime(0, ctx.currentTime)
  rampGain(target)
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useSoundscape(scene: RoundPhase | 'lobby', muted: boolean) {
  const gestureReady = useHasGesture()
  const sceneRef = useRef<string | null>(null)
  const mutedRef = useRef(muted)
  useEffect(() => { mutedRef.current = muted }, [muted])

  useEffect(() => {
    if (!gestureReady || !ctx) return
    if (sceneRef.current === scene) return
    sceneRef.current = scene

    const stingSrc = STING_TRACKS[scene as keyof typeof STING_TRACKS]
    if (stingSrc) playSting(stingSrc, mutedRef.current)

    // A results screen in dead silence reads as the sound having broken,
    // especially after fifteen minutes of continuous audio. The game keeps a
    // quiet bed under it — the lobby ambient at 60% — until the player leaves,
    // which is what the state actually is: back at a menu, game over.
    const src = scene === 'ended' ? ENDED_TRACK : LOOP_TRACKS[scene] ?? null
    const target = scene === 'ended' ? ENDED_VOLUME : BASE_VOLUME

    // Lobby and post-game share the ambient bed, and both run on the element
    // path — see the note above getBed(). These are the two screens the PWA is
    // actually backgrounded from, so they are the two that must not leave a Web
    // Audio source running.
    if (src === ENDED_TRACK) {
      fadeOutLoop()   // hand off: nothing of the Web Audio graph may stay live
      playBed(mutedRef.current ? 0 : target)
      return
    }

    stopBed()
    if (!src) { rampGain(0); return }
    void playLoop(src, mutedRef.current ? 0 : target)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene, gestureReady])

  useEffect(() => {
    const s = sceneRef.current
    if (!s) return
    // The bed is an element, so muting it is a volume set, not a gain ramp.
    if (s === 'lobby' || s === 'ended') {
      playBed(muted ? 0 : (s === 'ended' ? ENDED_VOLUME : BASE_VOLUME))
      return
    }
    if (!ctx) return
    rampGain(muted ? 0 : BASE_VOLUME)
  }, [muted])

  // Suspending the whole context is the reliable way to go quiet in the
  // background — one switch for every node, and nothing left ringing because a
  // throttled timer never reached the line that would have paused it.
  //
  // Do not expect this to prevent the 375 Hz backgrounding beep, and do not
  // rebuild it trying to. Measurement showed iOS freezes an installed PWA
  // before this handler runs at all: after the "fix" that zeroed the gain
  // synchronously here shipped, the audio still faded over ~450ms of ordinary
  // music before locking into the loop — which it could not have done if this
  // code had executed. A scheduled ramp is worse still, since the automation is
  // evaluated by the very render thread that is stalling.
  //
  // The beep is avoided by not leaving a Web Audio source playing on the
  // screens the app is backgrounded from (see getBed), not from here. This
  // handler earns its keep on desktop and on tab-switching, where it does run.
  useEffect(() => {
    const silence = () => {
      if (!ctx) return
      // Zero the gain SYNCHRONOUSLY, then suspend in the same turn.
      //
      // A scheduled ramp plus a delayed suspend was tried and measurably made
      // this worse: iOS freezes the page at hide, so the timer never fired, the
      // context was never suspended, and the graph sat repeating its last
      // 128-frame quantum — measured at 2.7x the music's level for 470ms,
      // against 1/5 of it for 370ms when the suspend was immediate.
      //
      // Nothing scheduled can be relied on here. An instantaneous zero risks a
      // single-sample step, which is inaudible next to what it replaces, and it
      // guarantees that whatever quantum the hardware ends up looping is silent.
      if (loopGain) {
        loopGain.gain.cancelScheduledValues(ctx.currentTime)
        loopGain.gain.value = 0
      }
      void ctx.suspend()
    }

    const onVisibility = () => {
      if (!ctx) return
      if (document.hidden) { silence(); return }
      if (!sceneRef.current) return
      // The gain was ramped to zero on the way out, so resuming the context is
      // not enough on its own — without this the app comes back silent.
      void ctx.resume().then(() => {
        if (!sceneRef.current) return
        rampGain(mutedRef.current ? 0 : (sceneRef.current === 'ended' ? ENDED_VOLUME : BASE_VOLUME))
      }).catch(() => { /* resume needs a fresh gesture — the listener handles it */ })
    }

    // `pagehide` gets its own handler rather than sharing the one above. It can
    // fire while `document.hidden` is still false (bfcache navigation), which
    // would send it down the *resume* branch and ramp the volume back up on a
    // page that is going away — the opposite of what is wanted.
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('pagehide', silence)
    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('pagehide', silence)
    }
  }, [])

  useEffect(() => {
    return () => {
      loopToken++          // cancel any decode still in flight
      // Ramped, not cut — see fadeOutLoop. The old `stopLoop()` + `gain = 0`
      // here was the sharp click heard when leaving the lobby or a game.
      fadeOutLoop()
      sceneRef.current = null
      stopAllAudio()       // arena creak/heartbeat still use HTMLAudioElement
    }
  }, [])
}

// ── One-shot helper (game-over stings, eliminations etc.) ────────────────────
export function playSting(src: string, muted: boolean, volume = BASE_VOLUME + 0.15) {
  if (muted || !ctx) return
  void loadBuffer(src).then(buffer => {
    if (!ctx) return
    const source = ctx.createBufferSource()
    const gain = ctx.createGain()
    gain.gain.value = volume
    source.buffer = buffer
    source.connect(gain)
    gain.connect(ctx.destination)
    // Self-disposing: one-shots used to be built as elements nobody held a
    // reference to, which is how stings outlived the page that started them.
    source.onended = () => { source.disconnect(); gain.disconnect() }
    source.start()
  }).catch(() => {/* missing or undecodable — silence is fine */})
}
